import { Map } from "immutable";

import { Action } from "common/types/redux";

type Assignments<A> = Map<string, A>;
type AssignmentReducer<A> = (arg0: A, arg1: Action<any>) => A;
type ReceiveAssignmentActionCreator<
  R extends Record<string, any>,
  T extends string
> = (arg0: R) => {
  type: T;
  payload: R;
};
type AssignmentsReducerOptions<
  A,
  R extends Record<string, any>,
  T extends string
> = {
  assignmentReducer: AssignmentReducer<A>;
  receiveAssignmentActionType: T;
  receiveAssignmentsActionType: string;
  receiveAssignmentActionCreator: ReceiveAssignmentActionCreator<R, T>;
  deleteAssignmentsActionType: string;
};

function makeAssignmentsReducer<
  A,
  R extends Record<string, any>,
  T extends string
>({
  assignmentReducer,
  receiveAssignmentActionCreator,
  receiveAssignmentActionType,
  receiveAssignmentsActionType,
  deleteAssignmentsActionType
}: AssignmentsReducerOptions<A, R, T>) {
  return function assignmentsReducer(
    assignments: Assignments<A> = Map<string, A>(),
    action: Action<any>
  ) {
    switch (action.type) {
      case receiveAssignmentsActionType:
        return receiveAssignments(
          assignments,
          action,
          assignmentReducer,
          receiveAssignmentActionCreator
        );

      case receiveAssignmentActionType:
        return receiveAssignment(
          assignments,
          action.payload,
          assignmentReducer,
          receiveAssignmentActionCreator
        );

      case deleteAssignmentsActionType:
        return deleteAssignments(assignments, action.payload);

      default:
        return assignments;
    }
  };
}

function deleteAssignments(assignments: any, idsToDelete: any) {
  const newAssignments = assignments.withMutations((assignments: any) => {
    idsToDelete.forEach((id: any) => assignments.delete(id));
  });
  return newAssignments;
}

function callAssignmentReducer<A>(
  assignments: Assignments<A>,
  action: Record<string, any>,
  assignmentId: string,
  assignmentReducer: AssignmentReducer<A>
) {
  return assignments.update(assignmentId, assignment =>
    // @ts-ignore
    assignmentReducer(assignment!, action)
  );
}

function receiveAssignment<A, R extends Record<string, any>, T extends string>(
  assignments: Assignments<A>,
  assignment: R,
  assignmentReducer: AssignmentReducer<A>,
  receiveAssignmentActionCreator: ReceiveAssignmentActionCreator<R, T>
) {
  return callAssignmentReducer(
    assignments,
    receiveAssignmentActionCreator(assignment),
    assignment.id,
    assignmentReducer
  );
}

function receiveAssignments<A, R extends Record<string, any>, T extends string>(
  assignments: Assignments<A>,
  action: Record<string, any>,
  assignmentReducer: AssignmentReducer<A>,
  receiveAssignmentActionCreator: ReceiveAssignmentActionCreator<R, T>
) {
  const receivedAssignments = action.payload;
  return assignments.withMutations(assignments => {
    receivedAssignments.forEach((assignment: any) =>
      receiveAssignment(
        assignments,
        assignment,
        assignmentReducer,
        receiveAssignmentActionCreator
      )
    );
  });
}

export default makeAssignmentsReducer;
